import { PROFILE_LABEL_SX } from "../../profileFieldStyles";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { DeleteOutline as DeleteOutlineIcon } from "../../icons/FigmaControlIcons";
import StarIcon from "@mui/icons-material/Star";
import CloseIcon from "@mui/icons-material/Close";
import InfoActionIcons from "../../InfoActionIcons";
import CompanySizeDropdownField from "../../CompanySizeDropdownField";
import PopupMultiSelectField from "../../PopupMultiSelectField";
import LocationMultiSelectField from "../../LocationMultiSelectField";
import TabSectionHeader from "../../TabSectionHeader";
import {
  buildCompanySocialPayloadFromLinks,
  detectCompanySocialType,
  getCompanySocialLinksArray,
  getPrimaryCompanyWebsite,
  normalizeCompanySocialLinksArray,
} from "../../../utils/companySocialLinks";
import {
  getEntityLocationValues,
  groupLocationOption,
  normalizeLocationEntry,
  normalizeLocationFilterValues,
} from "../../../utils/locationFilters";
import {
  formatIndustrySelectionText,
  normalizeIndustrySelectionValues,
} from "../../../utils/industrySelections";
import { normalizeCompanySizeValue } from "../../../utils/companySizeOptions";

const API_BASE = process.env.REACT_APP_API_BASE;
const COMPANY_MULTI_VALUE_SEPARATOR = " | ";
const WORK_POLICY_OPTIONS = ["Remote", "Hybrid", "On-site"];
const PURPLE = "#9C4FFF";
const READ_ONLY_BORDER = "#D2D8DE";
const EDIT_MODE_BORDER = "#D9C2FF";
const EDIT_MODE_OUTLINE = "0 0 0 1px rgba(156,79,255,0.22)";

const getToken = () => {
  const direct =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("id_token");

  if (direct && String(direct).trim()) return String(direct).trim();

  const tryJsonKeys = ["user", "auth", "session", "profile"];
  for (const key of tryJsonKeys) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      const token =
        obj?.token ||
        obj?.accessToken ||
        obj?.access_token ||
        obj?.jwt ||
        obj?.idToken ||
        obj?.id_token;
      if (token && String(token).trim()) return String(token).trim();
    } catch {
      // ignore malformed session data
    }
  }

  return "";
};

const getAuthHeaders = () => {
  const token = getToken();
  if (!token) return {};

  const rawToken = token.replace(/^Bearer\s+/i, "");
  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
};

const labelStyle = PROFILE_LABEL_SX;

const normalizeOptionText = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

const normalizeWorkPolicyOption = (value = "") => {
  const normalized = normalizeOptionText(value).toLowerCase();
  if (!normalized) return "";
  if (normalized === "remote") return "Remote";
  if (normalized === "hybrid" || normalized === "hibryd") return "Hybrid";
  if (normalized === "on-site" || normalized === "onsite" || normalized === "on site") {
    return "On-site";
  }

  return "";
};

const normalizeWorkPolicyValues = (value = []) => {
  const rawValues = Array.isArray(value) ? value : parseStringArray(value);
  const seen = new Set();
  const nextValues = [];

  rawValues.forEach((item) => {
    const normalized = normalizeWorkPolicyOption(item);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    nextValues.push(normalized);
  });

  return WORK_POLICY_OPTIONS.filter((option) => nextValues.includes(option));
};

const mergeUniqueTextOptions = (...groups) => {
  const seen = new Set();
  const next = [];

  groups.flat().forEach((value) => {
    const label = normalizeOptionText(value);
    const key = label.toLowerCase();
    if (!label || seen.has(key)) return;
    seen.add(key);
    next.push(label);
  });

  return next.sort((left, right) => left.localeCompare(right));
};

const parseStringArray = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item ?? "").trim()).filter(Boolean);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item ?? "").trim()).filter(Boolean);
      }
    } catch {
      // fall back to delimited parsing
    }

    return trimmed
      .split(/\s*(?:,|\|)\s*/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const formatSelectionDisplayText = (values = []) => mergeUniqueTextOptions(values).join(", ");

const renderReadOnlyChips = (values = []) => {
  const visibleValues = Array.isArray(values)
    ? values.map((value) => normalizeOptionText(value)).filter(Boolean)
    : [];

  if (!visibleValues.length) {
    return (
      <Typography
        sx={{
          mt: 1,
          fontSize: "0.95rem",
          color: "#667085",
        }}
      >
        -
      </Typography>
    );
  }

  return (
    <Box sx={{ width: "100%", mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
      {visibleValues.map((value, index) => (
        <Chip
          key={`${value}-${index}`}
          label={value}
          sx={{
            background: "#EAF2FF",
            color: "#3451F4",
            fontWeight: 600,
            borderRadius: "999px",
          }}
        />
      ))}
    </Box>
  );
};

const parseCompanyLocationSelections = (company = {}) => {
  // Payloads can originate from list rows, detail routes, and optimistic
  // saves.  Read every supported representation instead of letting one
  // single-value alias hide a persisted multi-location list.
  return getEntityLocationValues(company);
};

const toRatingNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const buildStaticInfo = (company = {}) => {
  const locationValues = parseCompanyLocationSelections(company);
  const industryValues = normalizeIndustrySelectionValues(
    company.industry_sectors_json ??
      company.industries ??
      company.industry_sector_values ??
      company.industry_sector ??
      company.industry
  );

  return {
    company_name: company.name || company.company_name || company.companyName || "",
    lead_title:
      company.job_title ||
      company.jobTitle ||
      company.title ||
      company.role_title ||
      company.position_title ||
      "",
    owner: company.owner || company.owner_name || company.team_member || "",
    location: formatSelectionDisplayText(locationValues),
    location_values: locationValues,
    ownership_type: company.ownership_type || "",
    parent_company: company.parent_company || "",
    career_site_url:
      company.career_site_url ||
      company.career_site ||
      company.career_website ||
      company.company_career_website ||
      "",
    industry_sector: formatSelectionDisplayText(industryValues),
    industry_sector_values: industryValues,
    founded_year: company.founded_year || company.founded || "",
    company_size: normalizeCompanySizeValue(company.company_size || company.size_range || ""),
    company_website: getPrimaryCompanyWebsite(company) || "",
    work_policies_values: normalizeWorkPolicyValues(company.work_policies || []),
    work_policies: formatSelectionDisplayText(
      normalizeWorkPolicyValues(company.work_policies || [])
    ),
    email: company.company_email || company.contact_email || company.email || "",
    phone: company.company_phone || company.contact_phone || company.phone || "",
  };
};

const buildCompanySnapshot = (company = {}) => ({
  info: buildStaticInfo(company),
  recruitmentTools: parseStringArray(company.recruitment_tools || company.techStack),
  jobBoards: parseStringArray(company.job_boards),
  glassdoorRating: toRatingNumber(company.job_board_rating),
  socialLinks: getCompanySocialLinksArray(company),
});

const getFieldSurfaceSx = (editing = false, borderRadius = "8px") => ({
  width: "100%",
  mt: 2,
  borderRadius,
  background: "#fff",
  "& .MuiOutlinedInput-root": {
    borderRadius,
    backgroundColor: "#FFFFFF",
    boxShadow: editing ? EDIT_MODE_OUTLINE : "none",
    "& fieldset": {
      borderColor: editing ? EDIT_MODE_BORDER : READ_ONLY_BORDER,
      borderWidth: 1,
    },
    "&:hover fieldset": {
      borderColor: editing ? PURPLE : READ_ONLY_BORDER,
    },
    "&.Mui-focused fieldset": {
      borderColor: editing ? PURPLE : READ_ONLY_BORDER,
      borderWidth: 1,
    },
  },
  "& .MuiOutlinedInput-input": {
    fontFamily: "Be Vietnam Pro",
    fontSize: 13,
    fontWeight: 500,
    color: "#344054",
  },
  "& .MuiSelect-select": {
    fontFamily: "Be Vietnam Pro",
    fontSize: 13,
    fontWeight: 500,
    color: "#344054",
  },
});

const Field = ({
  label,
  name,
  value,
  onChange,
  readOnly,
  sx,
  ...textFieldProps
}) => (
  <Box mb={4} sx={{ width: "100%" }}>
    <Typography className="profile-field-label" sx={labelStyle}>{label}:</Typography>
    <TextField
      variant="outlined"
      fullWidth
      size="small"
      name={name}
      type={/website|url|link/i.test(name || "") ? "url" : "text"}
      value={value || ""}
      onChange={onChange}
      InputProps={{ readOnly }}
      {...textFieldProps}
      sx={{
        ...getFieldSurfaceSx(!readOnly),
        ...sx,
      }}
    />
  </Box>
);

const WorkPoliciesField = ({ values = [], onChange, readOnly }) => {
  const normalizedValues = normalizeWorkPolicyValues(values);

  return (
    <Box mb={4} sx={{ width: "100%" }}>
      <Typography className="profile-field-label" sx={labelStyle}>Work Policies:</Typography>
      {readOnly ? (
        renderReadOnlyChips(normalizedValues)
      ) : (
        <TextField
          select
          variant="outlined"
          fullWidth
          size="small"
          name="work_policies_values"
          value={normalizedValues}
          onChange={onChange}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (selected) => {
              const selectedValues = normalizeWorkPolicyValues(selected);
              if (!selectedValues.length) return "Select work policies";
              return selectedValues.join(", ");
            },
          }}
          sx={{
            ...getFieldSurfaceSx(true),
            "& .MuiSvgIcon-root": {
              color: "#667085",
            },
          }}
        >
          {WORK_POLICY_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
};

const CompanyInformationSection = ({
  company = {},
  leadId,
  entityType = "lead",
  onCompanyChange,
  fieldOptions = {},
}) => {
  const isCompanyEntity = String(entityType || "lead").toLowerCase() === "company";
  const editableRecordId = isCompanyEntity
    ? company.company_id || company.companyId || company.id
    : leadId || company.lead_id || company.leadId || company.entity_id || company.id;
  const canEditAnything = Boolean(editableRecordId);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(() => buildStaticInfo(company));
  const [recruitmentTools, setRecruitmentTools] = useState(() =>
    parseStringArray(company.recruitment_tools || company.techStack)
  );
  const [jobBoards, setJobBoards] = useState(() => parseStringArray(company.job_boards));
  const [newRecruitmentTool, setNewRecruitmentTool] = useState("");
  const [newJobBoard, setNewJobBoard] = useState("");
  const [glassdoorRating, setGlassdoorRating] = useState(() =>
    toRatingNumber(company.job_board_rating)
  );
  const [, setSnapshot] = useState(() => buildCompanySnapshot(company));
  const [pendingLocationInput, setPendingLocationInput] = useState("");
  const [socialLinks, setSocialLinks] = useState(() =>
    buildCompanySnapshot(company).socialLinks
  );
  const [dbFieldOptions, setDbFieldOptions] = useState({ locations: [], industries: [] });
  const [loadingDbFieldOptions, setLoadingDbFieldOptions] = useState(false);
  const lastSyncedCompanyIdRef = React.useRef("");
  const lastHydratedCompanyLocationIdRef = React.useRef("");
  const infoRef = React.useRef(info);
  const editingRef = React.useRef(editing);
  const savingRef = React.useRef(saving);
  infoRef.current = info;

  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  const mergedFieldOptions = useMemo(
    () => ({
      ...fieldOptions,
      locations: normalizeLocationFilterValues([
        ...(fieldOptions.locations || []),
        ...(dbFieldOptions.locations || []),
      ]),
      industries: normalizeIndustrySelectionValues([
        ...(fieldOptions.industries || []),
        ...(dbFieldOptions.industries || []),
      ]),
    }),
    [dbFieldOptions.industries, dbFieldOptions.locations, fieldOptions]
  );

  useEffect(() => {
    if (!isCompanyEntity) return;

    const nextCompanyId = String(company?.id || company?.company_id || company?.companyId || "");
    const isSameCompany = lastSyncedCompanyIdRef.current === nextCompanyId;
    if (editingRef.current && isSameCompany && !savingRef.current) {
      return;
    }

    const nextSnapshot = buildCompanySnapshot(company);
    const currentLocationValues = normalizeLocationFilterValues(
      infoRef.current?.location_values || []
    );
    const incomingLocationValues = normalizeLocationFilterValues(
      nextSnapshot.info?.location_values || []
    );
    // A table row commonly only has the primary `location`, while the detail
    // response holds every value in `locations_json`.  Never let a later
    // same-company table refresh collapse an already-hydrated chip list.
    const resolvedLocationValues =
      isSameCompany && currentLocationValues.length > incomingLocationValues.length
        ? currentLocationValues
        : incomingLocationValues;
    const resolvedInfo = {
      ...nextSnapshot.info,
      location_values: resolvedLocationValues,
      location: formatSelectionDisplayText(resolvedLocationValues),
    };
    const resolvedSnapshot = {
      ...nextSnapshot,
      info: resolvedInfo,
    };
    lastSyncedCompanyIdRef.current = nextCompanyId;
    setInfo(resolvedInfo);
    setRecruitmentTools(nextSnapshot.recruitmentTools);
    setJobBoards(nextSnapshot.jobBoards);
    setGlassdoorRating(nextSnapshot.glassdoorRating);
    setSnapshot(resolvedSnapshot);
    setSocialLinks(nextSnapshot.socialLinks);
    setEditing(false);
    setSaving(false);
    setPendingLocationInput("");
    setNewRecruitmentTool("");
    setNewJobBoard("");
  }, [company, isCompanyEntity]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const fetchFieldOptions = async () => {
      setLoadingDbFieldOptions(true);
      try {
        const res = await fetch(`${API_BASE}/api/companies/field-options`, {
          signal: controller.signal,
          headers: {
            ...getAuthHeaders(),
          },
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch company field options (${res.status})`);
        }

        const json = await res.json().catch(() => ({}));
        if (!active) return;

        setDbFieldOptions({
          locations: normalizeLocationFilterValues(json?.locations || []),
          industries: normalizeIndustrySelectionValues(json?.industries || []),
        });
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Failed to fetch company field options:", error);
        }
      } finally {
        if (active) setLoadingDbFieldOptions(false);
      }
    };

    fetchFieldOptions();

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (isCompanyEntity) return undefined;

    const idToLoad =
      leadId || company.lead_id || company.leadId || company.entity_id || company.id;
    if (!idToLoad) {
      const nextSnapshot = buildCompanySnapshot(company);
      setInfo(nextSnapshot.info);
      setRecruitmentTools(nextSnapshot.recruitmentTools);
      setJobBoards(nextSnapshot.jobBoards);
      setGlassdoorRating(nextSnapshot.glassdoorRating);
      setSnapshot(nextSnapshot);
      setSocialLinks(nextSnapshot.socialLinks);
      setPendingLocationInput("");
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    const fetchInfo = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/leads/${idToLoad}/info`, {
          signal: controller.signal,
        });

        if (!res.ok) {
          console.error("[CompanyInfo] Failed to fetch company info", res.status);
          return;
        }

        const json = await res.json();
        if (!active) return;

        const loadedInfo = buildStaticInfo({ ...(company || {}), ...(json || {}) });

        const nextSnapshot = {
          info: loadedInfo,
          recruitmentTools: parseStringArray(json.recruitment_tools),
          jobBoards: parseStringArray(json.job_boards),
          glassdoorRating: toRatingNumber(json.job_board_rating),
          socialLinks: getCompanySocialLinksArray({ ...(company || {}), ...(json || {}) }),
        };

        setInfo(nextSnapshot.info);
        setRecruitmentTools(nextSnapshot.recruitmentTools);
        setJobBoards(nextSnapshot.jobBoards);
        setGlassdoorRating(nextSnapshot.glassdoorRating);
        setSnapshot(nextSnapshot);
        setSocialLinks(nextSnapshot.socialLinks);
        setPendingLocationInput("");
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("Error loading company info:", error);
      }
    };

    fetchInfo();

    return () => {
      active = false;
      controller.abort();
    };
  }, [leadId, company, isCompanyEntity]);

  useEffect(() => {
    // The parent detail view is the authoritative company hydration request.
    // Avoid a second request that used to update saved locations after the
    // rest of the profile had rendered.
    if (!isCompanyEntity || !editableRecordId) return undefined;

    const companyId = String(editableRecordId);
    if (lastHydratedCompanyLocationIdRef.current === companyId) return undefined;
    lastHydratedCompanyLocationIdRef.current = companyId;

    const companyAlreadyIncludesPersistedLocations = normalizeLocationFilterValues([
      company?.locations,
      company?.locations_json,
      company?.locationsJson,
      company?.location_values,
    ]).length > 0;
    if (companyAlreadyIncludesPersistedLocations) return undefined;

    const controller = new AbortController();
    let active = true;

    const hydratePersistedCompanyLocations = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/companies/${encodeURIComponent(companyId)}`, {
          signal: controller.signal,
          headers: {
            ...getAuthHeaders(),
          },
          credentials: "include",
        });
        if (!response.ok) return;

        const payload = await response.json().catch(() => ({}));
        const persistedCompany = payload?.company || payload;
        const persistedLocations = parseCompanyLocationSelections(persistedCompany);
        if (!active || editingRef.current || !persistedLocations.length) return;

        // A company can be opened from a compact table row that contains only
        // its primary `location`.  Replace just this field from the database
        // detail record so every saved location is visible without a page reload.
        setInfo((previous) => ({
          ...previous,
          location_values: persistedLocations,
          location: formatSelectionDisplayText(persistedLocations),
        }));
        setSnapshot((previous) => ({
          ...(previous || {}),
          info: {
            ...(previous?.info || {}),
            location_values: persistedLocations,
            location: formatSelectionDisplayText(persistedLocations),
          },
        }));
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.warn("Failed to hydrate company locations:", error);
        }
      }
    };

    hydratePersistedCompanyLocations();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    editableRecordId,
    isCompanyEntity,
    company?.location_values,
    company?.locations,
    company?.locations_json,
    company?.locationsJson,
  ]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    if (name === "work_policies_values") {
      const normalizedValues = normalizeWorkPolicyValues(value);
      setInfo((prev) => ({
        ...prev,
        work_policies_values: normalizedValues,
        work_policies: formatSelectionDisplayText(normalizedValues),
      }));
      return;
    }

    setInfo((prev) => ({
      ...prev,
      [name]: name === "company_size" ? normalizeCompanySizeValue(value) : value,
    }));
  };

  const handleAddRecruitmentTool = () => {
    const value = newRecruitmentTool.trim();
    if (!value) return;
    if (!recruitmentTools.includes(value)) {
      setRecruitmentTools((prev) => [...prev, value]);
    }
    setNewRecruitmentTool("");
  };

  const handleRemoveRecruitmentTool = (tool) => {
    setRecruitmentTools((prev) => prev.filter((item) => item !== tool));
  };

  const handleAddJobBoard = () => {
    const value = newJobBoard.trim();
    if (!value) return;
    if (!jobBoards.includes(value)) {
      setJobBoards((prev) => [...prev, value]);
    }
    setNewJobBoard("");
  };

  const handleRemoveJobBoard = (board) => {
    setJobBoards((prev) => prev.filter((item) => item !== board));
  };

  const handleStarClick = (value) => {
    if (!editing || !canEditAnything) return;
    setGlassdoorRating(value);
  };

  const updateSocialLinkAt = (index, value) => {
    setSocialLinks((prev) => {
      const current = Array.isArray(prev) ? [...prev] : [];
      while (current.length <= index) current.push("");
      current[index] = value;
      return current;
    });
  };

  const addSocialLinkField = () => {
    setSocialLinks((prev) => [...(Array.isArray(prev) ? prev : []), ""]);
  };

  const removeSocialLinkAt = (index) => {
    setSocialLinks((prev) =>
      Array.isArray(prev) ? prev.filter((_, itemIndex) => itemIndex !== index) : []
    );
  };

  const handleSave = async () => {
    const idToSave = editableRecordId;
    if (!idToSave || !canEditAnything) return;

    const normalizedLocations = normalizeLocationFilterValues([
      info.location_values || [],
      pendingLocationInput,
    ]);
    const normalizedIndustries = normalizeIndustrySelectionValues(
      info.industry_sector_values ||
        info.industries ||
        info.industry_sectors_json ||
        info.industry_sector
    );
    const nextInfo = {
      ...info,
      company_name: String(info.company_name || "").trim(),
      location_values: normalizedLocations,
      location: formatSelectionDisplayText(normalizedLocations),
      industry_sector_values: normalizedIndustries,
      industry_sector: formatSelectionDisplayText(normalizedIndustries),
      industries: normalizedIndustries,
      work_policies_values: normalizeWorkPolicyValues(
        info.work_policies_values || info.work_policies
      ),
      work_policies: formatSelectionDisplayText(
        normalizeWorkPolicyValues(info.work_policies_values || info.work_policies)
      ),
      email: String(info.email || "").trim(),
      phone: String(info.phone || "").trim(),
    };

    if (!nextInfo.company_name) {
      window.alert("Company name is required.");
      return;
    }

    const serializedIndustries =
      formatIndustrySelectionText(normalizedIndustries) ||
      normalizedIndustries.join(COMPANY_MULTI_VALUE_SEPARATOR);
    const normalizedSocialLinks = normalizeCompanySocialLinksArray(socialLinks);
    const socialPayload = buildCompanySocialPayloadFromLinks(normalizedSocialLinks);
    const persistedSocialLinks = normalizedSocialLinks;
    const nextLocationState = {
      locations: nextInfo.location_values || [],
      locations_json: nextInfo.location_values || [],
      locationsJson: nextInfo.location_values || [],
      location_values: nextInfo.location_values || [],
    };

    setSaving(true);
    try {
      const res = await fetch(
        isCompanyEntity
          ? `${API_BASE}/api/companies/${idToSave}/info`
          : `${API_BASE}/api/leads/${idToSave}/info`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(isCompanyEntity ? getAuthHeaders() : {}),
          },
          credentials: isCompanyEntity ? "include" : "same-origin",
          body: JSON.stringify(
            isCompanyEntity
              ? {
                  name: nextInfo.company_name,
                  company_name: nextInfo.company_name,
                  owner: nextInfo.owner,
                  owner_name: nextInfo.owner,
                  team_member: nextInfo.owner,
                  location: nextInfo.location_values?.[0] || nextInfo.location,
                  locations: nextLocationState.locations,
                  ownership_type: nextInfo.ownership_type,
                  parent_company: nextInfo.parent_company,
                  career_site_url: nextInfo.career_site_url,
                  industries: normalizedIndustries,
                  industry_sectors_json: normalizedIndustries,
                  industry_sector: serializedIndustries,
                  founded_year: nextInfo.founded_year,
                  company_size: nextInfo.company_size,
                  company_website: nextInfo.company_website,
                  work_policies: nextInfo.work_policies,
                  company_email: nextInfo.email,
                  email: nextInfo.email,
                  company_phone: nextInfo.phone,
                  phone: nextInfo.phone,
                  social_links: normalizedSocialLinks,
                  social_links_text: JSON.stringify(normalizedSocialLinks),
                  ...socialPayload,
                  recruitment_tools: recruitmentTools,
                  job_boards: jobBoards,
                  job_board_rating: glassdoorRating,
                }
              : {
                  ...nextInfo,
                  name: nextInfo.company_name,
                  company_name: nextInfo.company_name,
                  job_title: nextInfo.lead_title,
                  location: nextInfo.location,
                  locations: nextInfo.location_values || [],
                  locations_json: nextInfo.location_values || [],
                  locationsJson: nextInfo.location_values || [],
                  industries: normalizedIndustries,
                  industry_sectors_json: normalizedIndustries,
                  industry_sector: serializedIndustries,
                  email: nextInfo.email,
                  phone: nextInfo.phone,
                  contact_email: nextInfo.email,
                  contact_phone: nextInfo.phone,
                  social_links: normalizedSocialLinks,
                  social_links_text: JSON.stringify(normalizedSocialLinks),
                  ...socialPayload,
                  recruitment_tools: recruitmentTools,
                  job_boards: jobBoards,
                  job_board_rating: glassdoorRating,
                }
          ),
        }
      );

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          payload?.error || `Failed to save ${isCompanyEntity ? "company" : "lead"} info.`
        );
      }

      if (isCompanyEntity) {
        // Rehydrate from the persisted record after saving.  This is an API
        // refresh, not a page reload, and prevents a partial list-row payload
        // from replacing the complete locations_json value in the open profile.
        let persistedCompany = payload?.company;
        try {
          const detailResponse = await fetch(`${API_BASE}/api/companies/${idToSave}`, {
            headers: {
              ...getAuthHeaders(),
            },
            credentials: "include",
          });
          if (detailResponse.ok) {
            const detailPayload = await detailResponse.json().catch(() => ({}));
            if (detailPayload?.company && typeof detailPayload.company === "object") {
              persistedCompany = detailPayload.company;
            }
          }
        } catch (refreshError) {
          // The PUT response is already a valid optimistic fallback.  Do not
          // report a failed refresh as a failed save.
          console.warn("Failed to refresh saved company locations:", refreshError);
        }

        const resolvedLocationValues = normalizeLocationFilterValues([
          persistedCompany?.locations,
          persistedCompany?.locations_json,
          persistedCompany?.locationsJson,
          persistedCompany?.location_values,
          nextLocationState.location_values || [],
          persistedCompany?.location,
        ]);
        const nextCompany =
          persistedCompany && typeof persistedCompany === "object"
            ? {
                ...(company || {}),
                ...persistedCompany,
                location:
                  persistedCompany.location ||
                  resolvedLocationValues[0] ||
                  nextInfo.location_values?.[0] ||
                  nextInfo.location,
                locations: resolvedLocationValues,
                locations_json: resolvedLocationValues,
                locationsJson: resolvedLocationValues,
                location_values: resolvedLocationValues,
              }
            : {
                ...(company || {}),
                name: nextInfo.company_name,
                company_name: nextInfo.company_name,
                owner: nextInfo.owner,
                owner_name: nextInfo.owner,
                team_member: nextInfo.owner,
                location: nextInfo.location_values?.[0] || nextInfo.location,
                ...nextLocationState,
                email: nextInfo.email,
                company_email: nextInfo.email,
                contact_email: nextInfo.email,
                phone: nextInfo.phone,
                company_phone: nextInfo.phone,
                contact_phone: nextInfo.phone,
                social_links: persistedSocialLinks,
                social_links_text: JSON.stringify(persistedSocialLinks),
                ...socialPayload,
              };
        const nextSnapshot = buildCompanySnapshot(nextCompany);

        setInfo(nextSnapshot.info);
        setRecruitmentTools(nextSnapshot.recruitmentTools);
        setJobBoards(nextSnapshot.jobBoards);
        setGlassdoorRating(nextSnapshot.glassdoorRating);
        setSnapshot(nextSnapshot);
        setSocialLinks(nextSnapshot.socialLinks);
        setPendingLocationInput("");
        setEditing(false);
        onCompanyChange?.({
          ...(company || {}),
          ...nextCompany,
          entityType: "company",
        });
        return;
      }

      const nextSnapshot = {
        info: nextInfo,
        recruitmentTools,
        jobBoards,
        glassdoorRating,
        socialLinks: persistedSocialLinks,
      };

      setInfo(nextInfo);
      setSnapshot(nextSnapshot);
      setSocialLinks(persistedSocialLinks);
      setPendingLocationInput("");
      onCompanyChange?.({
        ...(company || {}),
        ...nextInfo,
        name: nextInfo.company_name,
        company_name: nextInfo.company_name,
        job_title: nextInfo.lead_title,
        jobTitle: nextInfo.lead_title,
        ...nextLocationState,
        owner: nextInfo.owner,
        owner_name: nextInfo.owner,
        email: nextInfo.email,
        contact_email: nextInfo.email,
        phone: nextInfo.phone,
        contact_phone: nextInfo.phone,
        ...socialPayload,
        social_links: persistedSocialLinks,
        socialLinks: persistedSocialLinks,
        recruitment_tools: recruitmentTools,
        job_boards: jobBoards,
        job_board_rating: glassdoorRating,
        entityType: "lead",
      });
      setEditing(false);
    } catch (error) {
      console.error("Error saving company info:", error);
      window.alert(error?.message || "Failed to save company information.");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = () => {
    if (!canEditAnything) return;
    setEditing(true);
  };

  const readOnly = !editing || !canEditAnything;
  const socialLinksToRender = useMemo(
    () => (socialLinks.length ? socialLinks : editing ? [""] : []),
    [editing, socialLinks]
  );

  return (
    <Card sx={{ mb: 3, borderRadius: 0, boxShadow: "none" }}>
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <TabSectionHeader
          title="Company Information"
          rightText={canEditAnything ? (saving ? "Saving..." : editing ? "Save" : "Edit") : ""}
          onAction={editing ? handleSave : handleStartEdit}
          actionDisabled={saving}
          dividerColor="#29ABFF"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            columnGap: 4,
          }}
        >
          <Box>
            <Field
              label="Company Name"
              name="company_name"
              value={info.company_name}
              onChange={handleFieldChange}
              readOnly={readOnly}
            />

            {!isCompanyEntity ? (
              <Field
                label="Lead Title"
                name="lead_title"
                value={info.lead_title}
                onChange={handleFieldChange}
                readOnly={readOnly}
              />
            ) : null}

            <Box mb={4} sx={{ width: "100%" }}>
              <Typography className="profile-field-label" sx={labelStyle}>Location:</Typography>
              {readOnly ? (
                renderReadOnlyChips(info.location_values)
              ) : (
                <LocationMultiSelectField
                  value={normalizeLocationFilterValues(info.location_values || [])}
                  onInputValueChange={setPendingLocationInput}
                  onChange={(nextValue) => {
                    const normalized = normalizeLocationFilterValues(
                      (Array.isArray(nextValue) ? nextValue : []).map((value) =>
                        normalizeLocationEntry(value)
                      )
                    );
                    setInfo((prev) => ({
                      ...prev,
                      location_values: normalized,
                      location: formatSelectionDisplayText(normalized),
                    }));
                    setPendingLocationInput("");
                  }}
                  options={mergedFieldOptions.locations || []}
                  loading={loadingDbFieldOptions}
                  placeholder="Search or add locations"
                  inputSx={getFieldSurfaceSx(true)}
                  autocompleteProps={{
                    groupBy: groupLocationOption,
                    disabled: saving,
                    autoSelect: true,
                  }}
                />
              )}
            </Box>

            <Field
              label="Ownership type"
              name="ownership_type"
              value={info.ownership_type}
              onChange={handleFieldChange}
              readOnly={readOnly}
            />

            <Field
              label="Parent Company/Subsidiaries (if applicable)"
              name="parent_company"
              value={info.parent_company}
              onChange={handleFieldChange}
              readOnly={readOnly}
            />

            <Field
              label="Company Career Website"
              name="career_site_url"
              value={info.career_site_url}
              onChange={handleFieldChange}
              readOnly={readOnly}
            />

            <Field
              label="Email"
              name="email"
              value={info.email}
              onChange={handleFieldChange}
              readOnly={readOnly}
              type="email"
              placeholder="email@company.com"
            />

            <Field
              label="Phone Number"
              name="phone"
              value={info.phone}
              onChange={handleFieldChange}
              readOnly={readOnly}
              placeholder="+1 (555) 123-4567"
            />

            <Box mt={2}>
              <Typography className="profile-field-label" sx={labelStyle}>Glassdoor Rating:</Typography>
              <Box display="flex" alignItems="center" gap={0.3}>
                {[1, 2, 3, 4, 5].map((value) => (
                  <StarIcon
                    key={value}
                    onClick={() => handleStarClick(value)}
                    sx={{
                      color: value <= glassdoorRating ? "#4F46E5" : "#E2E8F0",
                      fontSize: "22px",
                      cursor: editing && canEditAnything ? "pointer" : "default",
                      transition: "transform 0.15s ease",
                      "&:hover":
                        editing && canEditAnything ? { transform: "scale(1.1)" } : undefined,
                    }}
                  />
                ))}
                <Typography variant="body2" sx={{ ml: 1, color: "#4A5568" }}>
                  {glassdoorRating ? `${glassdoorRating}.0 / 5` : "Not rated yet"}
                </Typography>
              </Box>
            </Box>

            <Box mt={2}>
              <Typography className="profile-field-label" sx={labelStyle}>Recruitment Tools:</Typography>

              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                {recruitmentTools.map((tool) => (
                  <Chip
                    key={tool}
                    label={tool}
                    onDelete={
                      editing && canEditAnything ? () => handleRemoveRecruitmentTool(tool) : undefined
                    }
                    deleteIcon={
                      editing && canEditAnything ? <CloseIcon sx={{ fontSize: 16 }} /> : undefined
                    }
                    sx={{
                      background: "#EEF2FF",
                      color: "#4F46E5",
                      fontWeight: 600,
                      borderRadius: "999px",
                    }}
                  />
                ))}

                {!recruitmentTools.length && !editing ? (
                  <Typography variant="body2" color="text.secondary">
                    No recruitment tools added yet.
                  </Typography>
                ) : null}
              </Box>

              {canEditAnything ? (
                editing ? (
                  <Box mt={1} display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      placeholder="Add a tool and press Enter"
                      value={newRecruitmentTool}
                      onChange={(event) => setNewRecruitmentTool(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleAddRecruitmentTool();
                        }
                      }}
                      sx={{ maxWidth: 260, ...getFieldSurfaceSx(true, "999px") }}
                    />
                    <Button
                      variant="text"
                      sx={{ p: 0, textTransform: "none", color: "#4F46E5" }}
                      onClick={handleAddRecruitmentTool}
                    >
                      Add New
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="text"
                    sx={{
                      mt: 1,
                      p: 0,
                      textTransform: "none",
                      color: "#A0AEC0",
                    }}
                    disabled
                  >
                    Add New
                  </Button>
                )
              ) : null}
            </Box>
          </Box>

          <Box>
            <Box mb={4} sx={{ width: "100%" }}>
              <Typography className="profile-field-label" sx={labelStyle}>Industry/Sector:</Typography>
              {readOnly ? (
                renderReadOnlyChips(info.industry_sector_values)
              ) : (
                <PopupMultiSelectField
                  value={info.industry_sector_values || []}
                  onChange={(nextValue) => {
                    const normalized = normalizeIndustrySelectionValues(nextValue || []);
                    setInfo((prev) => ({
                      ...prev,
                      industry_sector_values: normalized,
                      industry_sector: formatSelectionDisplayText(normalized),
                      industries: normalized,
                    }));
                  }}
                  options={mergedFieldOptions.industries || []}
                  loading={loadingDbFieldOptions}
                  placeholder="Search or add industries"
                  inputSx={getFieldSurfaceSx(true)}
                  autocompleteProps={{ disabled: saving }}
                />
              )}
            </Box>

            <Field
              label="Founded Year"
              name="founded_year"
              value={info.founded_year}
              onChange={handleFieldChange}
              readOnly={readOnly}
              fullWidth
            />

            {readOnly ? (
              <Field
                label="Company size"
                name="company_size"
                value={info.company_size}
                onChange={handleFieldChange}
                readOnly={readOnly}
                fullWidth
              />
            ) : (
              <Box mb={4} sx={{ width: "100%" }}>
                <Typography className="profile-field-label" sx={labelStyle}>Company size:</Typography>
                <CompanySizeDropdownField
                  value={info.company_size}
                  onChange={(nextValue) =>
                    setInfo((prev) => ({
                      ...prev,
                      company_size: nextValue,
                    }))
                  }
                  placeholder="50 - 200 employees"
                  inputSx={getFieldSurfaceSx(true)}
                />
              </Box>
            )}

            <Field
              label="Company Website"
              name="company_website"
              value={info.company_website}
              onChange={handleFieldChange}
              readOnly={readOnly}
            />

            <WorkPoliciesField
              values={info.work_policies_values || []}
              onChange={handleFieldChange}
              readOnly={readOnly}
            />

            <Box mt={2}>
              <Typography className="profile-field-label" sx={labelStyle}>Social Media:</Typography>

              <Box mt={1} display="grid" gap={1}>
                {socialLinksToRender.length ? (
                  socialLinksToRender.map((link, index) => {
                    const socialType = detectCompanySocialType(link, "website");

                    return (
                      <Box
                        key={`company-social-link-${index}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: editing ? { xs: "1fr", md: "1fr 96px" } : "1fr",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          variant="outlined"
                          fullWidth
                          size="small"
                          type="url"
                          value={link}
                          onChange={(event) => updateSocialLinkAt(index, event.target.value)}
                          InputProps={{
                            readOnly,
                            startAdornment: (
                              <InputAdornment position="start" sx={{ pointerEvents: "none" }}>
                                <InfoActionIcons
                                  actions={[{ type: socialType, href: link }]}
                                  showDisabled={true}
                                  showGroupDivider={false}
                                  gap={0}
                                />
                              </InputAdornment>
                            ),
                          }}
                          placeholder="https://linkedin.com/company/acme or https://x.com/acme"
                          sx={getFieldSurfaceSx(editing)}
                        />

                        {editing ? (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            sx={{
                              textTransform: "none",
                              borderRadius: 999,
                              color: "#EF4444",
                              borderColor: "#EF4444",
                              "&:hover": {
                                borderColor: "#EF4444",
                              },
                            }}
                            onClick={() => removeSocialLinkAt(index)}
                          >
                            Delete
                          </Button>
                        ) : null}
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No social links added yet.
                  </Typography>
                )}
              </Box>

              {editing ? (
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  sx={{ mt: 1, px: 0, textTransform: "none", color: "#4F46E5" }}
                  onClick={addSocialLinkField}
                >
                  Add Social Link
                </Button>
              ) : null}

              <Typography variant="caption" sx={{ mt: 0.75, display: "block", color: "#667085" }}>
                Paste a LinkedIn, Facebook, X, Google, Instagram, YouTube, or TikTok URL and the icon updates automatically.
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography className="profile-field-label" sx={labelStyle}>Job Boards:</Typography>

              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                {jobBoards.map((board) => (
                  <Chip
                    key={board}
                    label={board}
                    onDelete={
                      editing && canEditAnything ? () => handleRemoveJobBoard(board) : undefined
                    }
                    deleteIcon={
                      editing && canEditAnything ? <CloseIcon sx={{ fontSize: 16 }} /> : undefined
                    }
                    sx={{
                      background: "#EEF2FF",
                      color: "#4F46E5",
                      fontWeight: 600,
                      borderRadius: "999px",
                    }}
                  />
                ))}

                {!jobBoards.length && !editing ? (
                  <Typography variant="body2" color="text.secondary">
                    No job boards added yet.
                  </Typography>
                ) : null}
              </Box>

              {canEditAnything ? (
                editing ? (
                  <Box mt={1} display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      placeholder="Add a job board and press Enter"
                      value={newJobBoard}
                      onChange={(event) => setNewJobBoard(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleAddJobBoard();
                        }
                      }}
                      sx={{ maxWidth: 260, ...getFieldSurfaceSx(true, "999px") }}
                    />
                    <Button
                      variant="text"
                      sx={{ p: 0, textTransform: "none", color: "#4F46E5" }}
                      onClick={handleAddJobBoard}
                    >
                      Add New
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="text"
                    sx={{
                      mt: 1,
                      p: 0,
                      textTransform: "none",
                      color: "#A0AEC0",
                    }}
                    disabled
                  >
                    Add New
                  </Button>
                )
              ) : null}
            </Box>
          </Box>
        </Box>

      </CardContent>
    </Card>
  );
};

export default CompanyInformationSection;
